import { useCallback, useEffect, useState } from "react";
import { Store } from "../../../types";
import { createLog, updateStoreSyncType } from "../../../lib/admin";
import { Divider } from "@mui/material";
import InfoWarningIcon from "../../../assets/InfoWarningIcon";
import TriangularWarningIcon from "../../../assets/TriangularWarningIcon";
import StickySaveBar from "../../global/StickySaveBar";
import Notification from "../../global/Notification";
import { useHasScope } from "../../../utils/Permission";
import syncTypeIcon from "../../../assets/management/gear.svg";

type Props =
    {
        store: Store,
        hasUnsavedChanges: boolean,
        setHasUnsavedChanges: (flag: boolean) => void,
        hasBeenUpdated: boolean,
        setHasBeenUpdated: (flag: boolean) => void
    }

export default function StoreSyncFormType({ store, setHasUnsavedChanges, setHasBeenUpdated }: Props) {
    const [baseline, setBaseline] = useState(store.developmentPhase);
    const [syncType, setSyncType] = useState(store.developmentPhase);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const hasUpdateStoreSync = useHasScope('update:synctype');

    const dismissSaveError = useCallback(() => setSaveError(null), []);
    const closeSuccessMessage = useCallback(() => setSuccessMessage(null), []);

    const hasChanges = syncType !== baseline;

    useEffect(() => {
        setHasUnsavedChanges(hasChanges);
    }, [hasChanges, setHasUnsavedChanges]);

    const handleSave = async () => {
        if (!hasUpdateStoreSync) {
            setSaveError("You do not have permission to update this store.");
            return;
        }
        const newType = syncType;
        setSaveError(null);
        setSaving(true);
        try {
            await updateStoreSyncType(store.id, newType);
            createLog({
                storeId: store.id,
                entityType: 'sync',
                section: 'syncType',
                action: 'save',
                lastValue: { syncType: baseline },
                newValue: { syncType: newType },
            });
            setBaseline(newType);
            setSuccessMessage("Changes saved successfully.");
            setHasBeenUpdated(true);
        } catch (error) {
            console.error("Error saving sync type:", error);
            setSaveError("An error occurred. Please try again later.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelChanges = () => {
        setSyncType(baseline);
        setSaveError(null);
    };

    return (
        <div className="sync-management">
            <div className="sync-header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={syncTypeIcon} alt="" />Sync Type</h1>
            </div>
            <Divider className="divider" />
            <div className="form-container">
                <div className="warning-box">
                    <InfoWarningIcon></InfoWarningIcon>
                    <div className="content">
                        <p><strong>Important:</strong> This setting affects how synchronizer will work for this account from now on.</p>
                        <p>We will gradually migrate all V1 features to V2, but for the moment it is all a work in progress, so it is possible that some bugs may appear during this process.</p>
                        <p>If you are not sure, please contact the development team.</p>
                    </div>
                </div>
                <div className="synctype-container">
                    <button className={`regular ${syncType == 1 ? 'active' : ''}`} onClick={() => setSyncType(1)}>Legacy</button>
                    <button className={`v2 ${syncType == 2 ? 'active' : ''}`} onClick={() => setSyncType(2)}>V2</button>
                    <button className={`v2-beta ${syncType == 3 ? 'active' : ''}`} onClick={() => setSyncType(3)}>V2 Beta</button>
                </div>
                {!syncType && (
                    <div className="synctype-about">
                        <p><strong>This store has no sync type set. Select a sync type to view its characteristics.</strong></p>
                    </div>
                )}
                {syncType != null && (
                    <div className="synctype-about">
                        <p className='title'><strong>About this option:</strong></p>
                        {
                            syncType == 1 && (
                                <div className="about-content">
                                    <p><strong>Legacy (V1)</strong></p>
                                    <p>The original version, all features are working, but nothing new is added.</p>
                                    <p>This version is not maintained anymore, some small fixes only if the feature is not available in V2.</p>
                                </div>
                            )
                        }
                        {
                            syncType == 2 && (
                                <div className="about-content">
                                    <p><strong>V2</strong></p>
                                    <p>This version contains only some features, like products and products/categories synchronization.</p>
                                    <p>Everything else will still be running in Legacy version, until it is developed here. All new developments will be first tested in Beta phase.</p>
                                </div>
                            )
                        }
                        {
                            syncType == 3 && (
                                <div className="about-content">
                                    <p><strong>V2 Beta</strong></p>
                                    <div className="about-warning-box">
                                        <TriangularWarningIcon></TriangularWarningIcon>
                                        <div className="content">
                                            <p><strong>Warning: </strong>This is an unstable version, made only for testing new features in V2.</p>
                                            <p>Consider using only in development stores, or if you really need a new feature only existent in this phase.</p>
                                            <p>When the feature is correctly tested, will be moved to V2 and new features will be added to this phase.</p>
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        <a href="https://retailsystem-dev.notion.site/Sync-V2-Migration-status-69ecd04b39f9419093261f0e6010d829" target="_blank" rel="noopener noreferrer">Check Migration Status</a>
                    </div>
                )}
            </div>
            <StickySaveBar
                visible={hasChanges}
                saving={saving}
                onSave={handleSave}
                onCancel={handleCancelChanges}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={successMessage} type="success" onClose={closeSuccessMessage} />
        </div>
    )
}